import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import medLogo from "../assets/icons/mediately-insights-logo.svg";
import { formatDateRange } from "./numbers";

export interface PDFGenerationOptions {
  filename: string;
  title?: string;
  subtitle?: string;
  headerInfo?: {
    from?: string;
    until?: string;
    title?: string;
    adDetails?: {
      image?: string;
      published_from?: string;
      published_until?: string;
    };
  };
  includeLogo?: boolean;
}

const convertSvgToDataUrl = async (svgPath: string): Promise<string> => {
  try {
    const response = await fetch(svgPath);
    const svgText = await response.text();

    // Create a temporary canvas to convert SVG to PNG
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    return await new Promise((resolve, reject) => {
      img.onload = () => {
        canvas.width = img.width || 364;
        canvas.height = img.height || 41;
        ctx?.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      };

      img.onerror = () => reject(new Error("Failed to load SVG"));

      // Convert SVG to data URL
      const svgBlob = new Blob([svgText], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(svgBlob);
      img.src = url;
    });
  } catch (error) {
    console.error("Error converting SVG to data URL:", error);
    throw error;
  }
};

const processElement = async (
  selector: string,
  pdf: jsPDF,
  contentWidth: number,
  currentY: number,
  pageHeight: number,
  margin: number
): Promise<number> => {
  const element = document.querySelector(selector) as HTMLElement;

  if (!element) {
    console.warn(`Element not found: ${selector}`);
    return currentY;
  }

  // Capture element as canvas with reduced scale for smaller file size
  const canvas = await html2canvas(element, {
    scale: 1, // Reduced from 2 to decrease file size
    useCORS: true,
    allowTaint: true,
    backgroundColor: "#ffffff",
    logging: false,
  });

  const imgData = canvas.toDataURL("image/jpeg", 0.8); // Use JPEG with 80% quality instead of PNG
  const imgWidth = contentWidth;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;

  let newY = currentY;

  // Check if we need a new page
  if (newY + imgHeight > pageHeight - margin) {
    pdf.addPage();
    newY = margin;
  }

  // Add image to PDF
  pdf.addImage(imgData, "JPEG", margin, newY, imgWidth, imgHeight);
  return newY + imgHeight + 10; // Add spacing between elements
};

export const generatePDFFromElements = async (
  elementSelectors: string[],
  options: PDFGenerationOptions
): Promise<void> => {
  try {
    // eslint-disable-next-line new-cap
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
      compress: true, // Enable PDF compression
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 10;
    const contentWidth = pageWidth - 2 * margin;

    let currentY = margin;

    // Add logo if requested
    if (options.includeLogo) {
      try {
        const logoDataUrl = await convertSvgToDataUrl(medLogo);
        const logoWidth = 80; // Scale down from original 364px
        const logoHeight = 9; // Scale down from original 41px

        pdf.addImage(logoDataUrl, "PNG", margin, currentY, logoWidth, logoHeight);
        currentY += logoHeight + 10; // Add spacing after logo
      } catch (error) {
        console.warn("Could not add logo to PDF:", error);
        // Continue without logo if it fails
      }
    }

    // Add header
    if (options.title) {
      // Create title as a high-quality canvas image to avoid jsPDF text rendering issues
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      if (ctx) {
        const baseFontSize = 8; // Base font size in pixels for the canvas
        const scale = 4; // Render at 4x resolution for sharpness
        const textFontSize = baseFontSize * scale;

        ctx.font = `bold ${textFontSize}px Helvetica, Arial, sans-serif`;

        // Function to wrap text onto the canvas
        const wrapText = (text: string, maxWidth: number) => {
          const words = text.split(" ");
          const lines: string[] = [];
          let currentLine = "";

          words.forEach((word) => {
            const testLine = currentLine ? `${currentLine} ${word}` : word;
            const metrics = ctx.measureText(testLine);
            if (metrics.width > maxWidth && currentLine) {
              lines.push(currentLine);
              currentLine = word;
            } else {
              currentLine = testLine;
            }
          });
          lines.push(currentLine);
          return lines;
        };

        const canvasWidth = contentWidth * scale;
        const titleLines = wrapText(options.title, canvasWidth);
        const canvasLineHeight = textFontSize * 1.2;

        // Set final canvas dimensions
        canvas.width = canvasWidth;
        canvas.height = titleLines.length * canvasLineHeight;

        // Fill background with white (prevents black box in JPEG)
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Re-apply font and draw text
        ctx.font = `bold ${textFontSize}px Helvetica, Arial, sans-serif`;
        ctx.fillStyle = "#000000";
        ctx.textAlign = "left";
        ctx.textBaseline = "top";

        titleLines.forEach((line, index) => {
          ctx.fillText(line, 0, index * canvasLineHeight);
        });

        // Convert canvas to high-quality PNG
        const titleImageData = canvas.toDataURL("image/png");
        const imgHeight = canvas.height / scale;

        // Add image to PDF
        pdf.addImage(titleImageData, "PNG", margin, currentY, contentWidth, imgHeight);
        currentY += imgHeight + 6; // Add spacing
      } else {
        // Fallback for safety, though unlikely to be hit
        pdf.text(options.title, margin, currentY);
        currentY += 12;
      }
    }

    // Add period info
    if (options.headerInfo?.from && options.headerInfo?.until) {
      pdf.setFontSize(10);
      const formattedDateRange = formatDateRange(options.headerInfo.from, options.headerInfo.until);
      pdf.text(`Period: ${formattedDateRange}`, margin, currentY);
      currentY += 8;
    }

    // Add ad details if provided
    if (options.headerInfo?.adDetails) {
      const { adDetails } = options.headerInfo;
      pdf.setFontSize(9);

      if (adDetails.published_from && adDetails.published_until) {
        const formattedDateRange = formatDateRange(adDetails.published_from, adDetails.published_until);
        pdf.text(`Cme Period: ${formattedDateRange}`, margin, currentY);
        currentY += 6;
      }

      // Add ad image if provided
      if (adDetails.image) {
        try {
          currentY += 5; // Add spacing before image

          // Create a promise to load the image with CORS workaround
          const loadImageWithProxy = async (src: string): Promise<string> => {
            try {
              // First, try to load with CORS and cache busting for Chrome/WebKit
              const img = new Image();
              img.crossOrigin = "anonymous";

              // Add cache-busting parameter to avoid Chrome/WebKit disk cache CORS issues
              const cacheBustedUrl = `${src}${src.includes("?") ? "&" : "?"}_cb=${Date.now()}`;

              const loadPromise = new Promise<HTMLImageElement>((resolve, reject) => {
                const timeout = setTimeout(() => {
                  reject(new Error(`Image load timeout: ${src}`));
                }, 10000);

                img.onload = () => {
                  clearTimeout(timeout);
                  resolve(img);
                };

                img.onerror = () => {
                  clearTimeout(timeout);
                  reject(new Error(`CORS or network error for: ${src}`));
                };

                img.src = cacheBustedUrl;
              });

              const loadedImg = await loadPromise;

              // Convert to canvas and return data URL
              const canvas = document.createElement("canvas");
              const ctx = canvas.getContext("2d");

              if (!ctx) {
                throw new Error("Could not get canvas context");
              }

              canvas.width = loadedImg.width;
              canvas.height = loadedImg.height;
              ctx.drawImage(loadedImg, 0, 0);

              return canvas.toDataURL("image/png");
            } catch (corsError) {
              console.warn("CORS with cache-busting failed, trying fetch approach:", corsError);

              // Fallback: Try to fetch through API proxy
              try {
                const response = await fetch(
                  `${process.env.REACT_APP_API_SERVER}/v1/proxy-image?url=${encodeURIComponent(src)}`
                );
                if (!response.ok) {
                  throw new Error(`Proxy fetch failed: ${response.status}`);
                }
                const blob = await response.blob();
                return new Promise((resolve, reject) => {
                  const reader = new FileReader();
                  reader.onload = () => resolve(reader.result as string);
                  reader.onerror = () => reject(new Error("Failed to read blob"));
                  reader.readAsDataURL(blob);
                });
              } catch (proxyError) {
                console.warn("Proxy fetch failed, trying without CORS:", proxyError);

                // Final fallback: Try loading without CORS but with cache busting
                try {
                  const img = new Image();
                  // Don't set crossOrigin to avoid CORS preflight
                  const cacheBustedUrl = `${src}${src.includes("?") ? "&" : "?"}_cb=${Date.now()}`;

                  const finalLoadPromise = new Promise<HTMLImageElement>((resolve, reject) => {
                    const timeout = setTimeout(() => {
                      reject(new Error(`Final image load timeout: ${src}`));
                    }, 10000);

                    img.onload = () => {
                      clearTimeout(timeout);
                      resolve(img);
                    };

                    img.onerror = () => {
                      clearTimeout(timeout);
                      reject(new Error(`Final load failed: ${src}`));
                    };

                    img.src = cacheBustedUrl;
                  });

                  const finalImg = await finalLoadPromise;

                  const canvas = document.createElement("canvas");
                  const ctx = canvas.getContext("2d");

                  if (!ctx) {
                    throw new Error("Could not get canvas context");
                  }

                  canvas.width = finalImg.width;
                  canvas.height = finalImg.height;
                  ctx.drawImage(finalImg, 0, 0);

                  return canvas.toDataURL("image/png");
                } catch (finalError) {
                  console.warn("Final fallback failed:", finalError);
                  throw new Error(`All image loading methods failed for: ${src}`);
                }
              }
            }
          };

          console.log("Loading ad image from:", adDetails.image);
          // Note: Using cache-busting to fix Chrome/WebKit CORS disk cache issues
          // See: https://dev.to/aws-builders/solutions-for-cors-errors-caused-by-webkits-disk-cache-16gn
          const imageDataUrl = await loadImageWithProxy(adDetails.image);
          console.log("Ad image loaded successfully");

          // Create a temporary image to get dimensions
          const tempImg = new Image();
          const imgDimensions = await new Promise<{ width: number; height: number }>((resolve, reject) => {
            tempImg.onload = () => resolve({ width: tempImg.width, height: tempImg.height });
            tempImg.onerror = () => reject(new Error("Failed to get image dimensions"));
            tempImg.src = imageDataUrl;
          });

          // Calculate image dimensions to fit within PDF
          const maxImageWidth = contentWidth * 0.6; // Use 60% of content width
          const maxImageHeight = 40; // Max height in mm

          const aspectRatio = imgDimensions.width / imgDimensions.height;
          let imageWidth = Math.min(maxImageWidth, imgDimensions.width * 0.1); // Scale down
          let imageHeight = imageWidth / aspectRatio;

          // Adjust if height is too large
          if (imageHeight > maxImageHeight) {
            imageHeight = maxImageHeight;
            imageWidth = imageHeight * aspectRatio;
          }

          // Check if we need a new page for the image
          if (currentY + imageHeight > pageHeight - margin) {
            pdf.addPage();
            currentY = margin;
          }

          // Calculate centered position for the image
          const centeredX = (pageWidth - imageWidth) / 2;

          // Add image to PDF using the data URL, centered horizontally
          pdf.addImage(imageDataUrl, "PNG", centeredX, currentY, imageWidth, imageHeight);
          currentY += imageHeight + 5; // Add spacing after image
          console.log("Ad image added to PDF successfully");
        } catch (error) {
          console.warn("Could not add ad image to PDF:", error);
          // Add a text placeholder if image fails to load
          pdf.setFontSize(8);
          pdf.text("(Ad image could not be loaded due to CORS restrictions)", margin, currentY);
          currentY += 6;
        }
      }
    }

    currentY += 5; // Add spacing

    // Process elements sequentially
    await elementSelectors.reduce(async (yPromise, selector) => {
      const y = await yPromise;
      return processElement(selector, pdf, contentWidth, y, pageHeight, margin);
    }, Promise.resolve(currentY));

    // Save the PDF
    pdf.save(options.filename);
  } catch (error) {
    console.error("Error generating PDF:", error);
    throw new Error("Failed to generate PDF");
  }
};

export const generateReportPDF = async (reportData: {
  title: string;
  period: { from: string; until: string };
  type: string;
  periodOption?: string;
  adDetails?: {
    title?: string;
    image?: string;
    published_from?: string;
    published_until?: string;
    company?: string;
  };
}): Promise<void> => {
  const filename = `${reportData.type}_report_${reportData.period.from}_${reportData.period.until}.pdf`;

  // Define selectors for report elements
  const elementSelectors = [
    '[data-pdf_report="report-chart"]', // Chart container
    '[data-pdf_report="report-period-table"]', // Period table
    '[data-pdf_report="report-specializations-table"]', // Specializations table
  ];

  await generatePDFFromElements(elementSelectors, {
    filename,
    title: reportData.title,
    headerInfo: {
      from: reportData.period.from,
      until: reportData.period.until,
      adDetails: reportData.adDetails,
    },
    includeLogo: true,
  });
};
